import { type Workspace, workspaceColorsMap } from '@carboncopyco/types';
import type { AvatarProps, AvatarSize } from '@carboncopyco/ui';
import { Avatar, TooltipBubble } from '@carboncopyco/ui';
import { useMemo } from 'react';
import { Badge, type BadgeSize } from './Badge';
import { WorkspaceTypeIcon } from './WorkspaceTypeIcon';

export interface WorkspaceAvatarProps extends Pick<AvatarProps, 'size'> {
  workspace: Partial<Workspace>;
  iconTooltip?: boolean;
}

const avatarSizeToBadgeSize: Record<AvatarSize, BadgeSize> = {
  xxlarge: 'large',
  xlarge: 'large',
  large: 'large',
  medium: 'medium',
  small: 'small',
  xsmall: 'small',
};

export const WorkspaceAvatar = ({
  workspace,
  size = 'large',
  iconTooltip,
}: WorkspaceAvatarProps) => {
  const inverseColorMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(workspaceColorsMap).map(([key, val]) => [val, key])
      ),
    []
  );

  return (
    <Badge
      show={workspace.workspaceType === 'private'}
      size={avatarSizeToBadgeSize[size]}
      content={
        <div className="text-ds-natural-700">
          <TooltipBubble
            placement="top"
            title="Private workspace"
            disabled={!iconTooltip || workspace.workspaceType !== 'private'}
          >
            <WorkspaceTypeIcon
              workspaceType={workspace.workspaceType}
              size="100%"
            />
          </TooltipBubble>
        </div>
      }
    >
      <Avatar
        initials={workspace.workspaceName[0]}
        shape="squared"
        size={size}
        backgroundColor={
          workspace?.isDeleted
            ? 'bg-ds-natural-400'
            : workspaceColorsMap[workspace.color]
              ? workspace.color
              : inverseColorMap[workspace.color]
        }
      />
    </Badge>
  );
};
